import React, { useState, useEffect } from "react";
import { Modal, Input, Upload, message, Spin, Tooltip } from "antd";
import { InboxOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { UploadModal } from "./Timesheet.style";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Dragger } = Upload;

const TimesheetDetailsModal = ({ 
  visible, 
  timesheet, 
  mode = "edit",
  userRole = "Admin",
  onClose, 
  onSubmit 
}) => {
  const [leaveDateInput, setLeaveDateInput] = useState("");
  const [leaveDates, setLeaveDates] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [maxPaidLeaves, setMaxPaidLeaves] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    if (timesheet && visible) {
      setLeaveDates(timesheet.leaveTaken?.dates || []);
      
      const dateNumbers = (timesheet.leaveTaken?.dates || []).map(dateStr => {
        return parseInt(dateStr.split('-')[2], 10);
      }).join(', ');
      setLeaveDateInput(dateNumbers);
      
      fetchWorkSettings();
    }
  }, [timesheet, visible]);

  const fetchWorkSettings = async () => {
    setLoadingSettings(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams();
      if (timesheet?.partnerOrg) {
        params.append('partnerOrganization', timesheet.partnerOrg);
      }
      if (timesheet?.customerOrganization) {
        params.append('customerOrganization', timesheet.customerOrganization);
      }
      
      const response = await axios.get(
        `${API_CONST.GET_EFFECTIVE_WORK_SETTINGS}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log("Fetched work settings:", response.data);
      const settings = response.data.settings || response.data;
      setMaxPaidLeaves(settings.paidLeavesPerMonth ?? 2);
    } catch (error) {
      console.error("Error fetching work settings:", error);
      message.error("Failed to load work settings");
      setMaxPaidLeaves(1);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleClose = () => {
    setLeaveDateInput("");
    setLeaveDates([]);
    setUploadedFile(null);
    onClose();
  };

  const handleSubmit = () => {
    if (mode === "view") {
      handleClose();
      return;
    }

    const actualPaidLeave = Math.min(leaveDates.length, maxPaidLeaves);

    onSubmit({
      leaveDates,
      paidLeaveCount: actualPaidLeave,
      timesheetFile: uploadedFile
    });

    handleClose();
  };

  const handleLeaveDateChange = (e) => {
    const inputValue = e.target.value;
    setLeaveDateInput(inputValue);
    
    const MONTH_NUMBER = {
      January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
      July: 7, August: 8, September: 9, October: 10, November: 11, December: 12,
    };
    const dateNumbers = inputValue.split(',').map(d => d.trim()).filter(d => d && !isNaN(Number(d)));
    const monthNum = MONTH_NUMBER[timesheet.month] ?? 1;
    const monthStr = String(monthNum).padStart(2, '0');
    const dates = dateNumbers.map(day => {
      const dayStr = String(parseInt(day, 10)).padStart(2, '0');
      return `${timesheet.year}-${monthStr}-${dayStr}`;
    });
    setLeaveDates(dates);
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv,.xlsx,.xls,.pdf",
    beforeUpload: (file) => {
      const isAllowed = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                     file.type === "application/vnd.ms-excel" ||
                     file.type === "text/csv" ||
                     file.type === "application/pdf";
      if (!isAllowed) {
        message.error("You can only upload Excel, CSV, or PDF files!");
        return Upload.LIST_IGNORE;
      }
      setUploadedFile(file);
      return false;
    },
    onRemove: () => {
      setUploadedFile(null);
    },
    fileList: uploadedFile ? [uploadedFile] : [],
  };

  if (!timesheet) return null;

  const effectiveMaxPaidLeaves = maxPaidLeaves ?? 1;
  const actualPaidLeave = Math.min(leaveDates.length, effectiveMaxPaidLeaves);
  const unpaidLeaveDays = leaveDates.length - actualPaidLeave;
  const calculatedWorkingDays = timesheet.actualWorkingDays - unpaidLeaveDays;
  const calculatedBillableHours = calculatedWorkingDays * (timesheet.workingHoursPerDay || 8);
  
  const partnerRate = timesheet.partnerRate || timesheet.monthlyRate || 0;
  const clientRate = timesheet.clientRate || timesheet.monthlyRate || 0;
  
  const calculatedPartnerAmount = timesheet.actualWorkingDays > 0 
    ? (partnerRate / timesheet.actualWorkingDays) * calculatedWorkingDays 
    : 0;
  const calculatedClientAmount = timesheet.actualWorkingDays > 0 
    ? (clientRate / timesheet.actualWorkingDays) * calculatedWorkingDays 
    : 0;

  const isReadOnly = mode === "view";

  return (
    <Modal
      title={`${mode === "view" ? "View" : "Upload"} Timesheet - ${timesheet.talentName}`}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleClose}
      width={800}
      okText={mode === "view" ? "Close" : "Submit Timesheet"}
      cancelButtonProps={{ style: mode === "view" ? { display: "none" } : {} }}
    >
      <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}>
        {loadingSettings ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="Loading settings..." />
          </div>
        ) : (
          <UploadModal>
            <div>
              {timesheet.status === "rejected" && timesheet.rejectionReason && (
                <div style={{ 
                  marginBottom: 16, 
                  padding: "12px 16px", 
                  background: "#fff2f0", 
                  border: "1px solid #ffccc7",
                  borderRadius: 6 
                }}>
                  <div style={{ color: "#ff4d4f", fontWeight: 600, marginBottom: 4 }}>
                    Timesheet Rejected
                  </div>
                  <div style={{ color: "#434343" }}>
                    <strong>Reason:</strong> {timesheet.rejectionReason}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Talent Details</label>
                <div style={{ padding: "12px 16px", background: "#f8f9fd", borderRadius: 6 }}>
                  <div><strong>Role:</strong> {timesheet.role}</div>
                  <div><strong>Job ID:</strong> {timesheet.jobId || '—'}</div>
                  <div><strong>Month:</strong> {timesheet.month} {timesheet.year}</div>
                  <div><strong>Actual Working Days in Month:</strong> {timesheet.actualWorkingDays} days</div>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                  Leave Information
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                      Leave Dates (e.g., 5, 10, 15)
                    </label>
                    <Input
                      placeholder="Enter date numbers separated by commas"
                      value={leaveDateInput}
                      onChange={handleLeaveDateChange}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                      Max Paid Leaves (from Settings){" "}
                      <Tooltip title="This value is fetched from the organization's work settings or global default settings. It cannot be manually changed.">
                        <InfoCircleOutlined style={{ color: "#1890ff" }} />
                      </Tooltip>
                    </label>
                    <div style={{ 
                      padding: "8px 12px", 
                      background: "#f0f0f0", 
                      borderRadius: 6, 
                      border: "1px solid #d9d9d9",
                      color: "#595959"
                    }}>
                      {effectiveMaxPaidLeaves} days/month
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ padding: 16, background: "#fff5e6", borderRadius: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#014c75", marginBottom: 12 }}>
                    Leave Breakdown
                  </div>
                  <div style={{ fontSize: 13, lineHeight: "1.8" }}>
                    <div><strong>Total Leave Days:</strong> {leaveDates.length}</div>
                    <div><strong>Paid Leave (auto-calculated):</strong> {actualPaidLeave}</div>
                    <div style={{ color: "#ff6b6b" }}><strong>Unpaid Leave:</strong> {unpaidLeaveDays}</div>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
                    Paid leave = min(leave days, max paid leaves)
                  </div>
                </div>

                <div style={{ padding: 16, background: "#e7f6f2", borderRadius: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#014c75", marginBottom: 12 }}>
                    Billable Calculations
                  </div>
                  <div style={{ fontSize: 13, lineHeight: "1.8" }}>
                    <div><strong>Working Days:</strong> {calculatedWorkingDays} days</div>
                    <div><strong>Billable Hours:</strong> {calculatedBillableHours} hrs</div>
                    
                    {userRole === "Partner" && (
                      <div style={{ color: "#00d9a9", fontWeight: 600 }}>
                        <strong>Partner Rate:</strong> ₹ {Math.round(calculatedPartnerAmount).toLocaleString("en-IN")}
                      </div>
                    )}
                    
                    {userRole === "Customer" && (
                      <div style={{ color: "#00d9a9", fontWeight: 600 }}>
                        <strong>Client Rate:</strong> ₹ {Math.round(calculatedClientAmount).toLocaleString("en-IN")}
                      </div>
                    )}
                    
                    {userRole === "Admin" && (
                      <>
                        <div style={{ color: "#1890ff", fontWeight: 600 }}>
                          <strong>Client Rate:</strong> ₹ {Math.round(calculatedClientAmount).toLocaleString("en-IN")}
                        </div>
                        <div style={{ color: "#52c41a", fontWeight: 600 }}>
                          <strong>Partner Rate:</strong> ₹ {Math.round(calculatedPartnerAmount).toLocaleString("en-IN")}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {!isReadOnly && (
                <>
                  <div className="form-group" style={{ marginTop: 16 }}>
                    <label>Upload Timesheet File</label>
                    <Dragger {...uploadProps}>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ color: "#00d9a9" }} />
                      </p>
                      <p className="ant-upload-text">Click or drag file to this area to upload</p>
                      <p className="ant-upload-hint">
                        Support for Excel (.xlsx, .xls), CSV, or PDF files
                      </p>
                    </Dragger>
                  </div>

                  <div style={{ marginTop: 16, padding: 12, background: "#e7f6f2", borderRadius: 6, fontSize: 13 }}>
                    <strong style={{ color: "#014c75" }}>Note:</strong> Paid leave is automatically calculated based on the organization's settings (max {effectiveMaxPaidLeaves} days/month). Any leave days beyond this limit will be counted as unpaid leave.
                  </div>
                </>
              )}
            </div>
          </UploadModal>
        )}
      </div>
    </Modal>
  );
};

export default TimesheetDetailsModal;
